import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { emitSwUpdateStatus } from "src/utils/swUpdateEvents";
import { initVersionCheck } from "src/utils/versionCheck";
import { registrarAperturaSelectorFechaHora } from "src/utils/abrirSelectorFechaHora";

import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { FiscalizacionProvider } from "./components/Fiscalizacion/FiscalizacionContext";

const theme = createTheme({
  typography: {
    fontFamily: "Futura",
    h1: {
      color: "#404040",
      fontSize: 112,
      fontWeight: "lighter",
      lineHeight: "normal",
    },
    h2: {
      color: "#404040",
      fontSize: 56,
      fontWeight: "normal",
      lineHeight: "normal",
    },
    h3: {
      color: "#404040",
      fontSize: 45,
      fontWeight: "normal",
      lineHeight: "normal",
    },
    h4: {
      color: "#404040",
      fontSize: 34,
      fontWeight: "normal",
      lineHeight: "normal",
    },
    h5: {
      color: "#404040",
      fontSize: 24,
      fontWeight: "normal",
      lineHeight: "normal",
    },
    h6: {
      color: "#404040",
      fontSize: 20,
      fontWeight: "bold",
      lineHeight: "normal",
    },
    subtitle1: {
      color: "#404040",
      fontSize: 16,
      fontWeight: "normal",
      lineHeight: "normal",
    },
    subtitle2: {
      color: "#404040",
      fontSize: 14,
      fontWeight: "bold",
      lineHeight: "normal",
    },
    body1: {
      color: "#404040",
      fontSize: 14,
      fontWeight: "bold",
      lineHeight: "normal",
    },
    body2: {
      color: "#404040",
      fontSize: 14,
      fontWeight: "normal",
      lineHeight: "normal",
    },
    button: {
      color: "#404040",
      fontSize: 14,
      fontWeight: "bold",
      lineHeight: "normal",
      textTransform: "uppercase",
    },
    caption: {
      color: "#404040",
      fontSize: 12,
      fontWeight: "normal",
      lineHeight: "normal",
    },
    overline: {
      color: "#404040",
      fontSize: 14,
      fontWeight: "bold",
      lineHeight: "normal",
      textTransform: "uppercase",
    },
  },
  palette: {
    text: {
      disabled: "rgba(0, 0, 0, 0.5)",
    },
    primary: {
      light: "#9c1d48",
      main: "#910434",
      dark: "#80042e",
      contrastText: "#fff",
    },
    secondary: {
      light: "#ffc720",
      main: "#FFC107",
      dark: "#e0aa06",
      contrastText: "#000",
    },
    error: {
      light: "#cf3c4f",
      main: "#eb445a",
      dark: "#cf3c4f",
      contrastText: "white",
    },
  },
});

// Tocar cualquier parte de un campo de fecha/hora abre el selector (no solo el icono).
registrarAperturaSelectorFechaHora();

ReactDOM.render(
  <React.StrictMode>
    
    <MuiThemeProvider theme={theme}>
    <FiscalizacionProvider>
      <App />
      </FiscalizacionProvider>
    </MuiThemeProvider>
    
  </React.StrictMode>,
  document.getElementById("root")
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
const shouldRegisterSw = process.env.NODE_ENV !== 'test';

if (shouldRegisterSw) {
  // Estrategia de actualización NO invasiva:
  // - Solo al abrir la app: si hay update disponible, aplicarlo y recargar 1 vez.
  // - Durante el uso normal: no forzar updates ni recargas (no cortar al usuario).
  //
  // Nota: En PWAs instaladas (Android/iOS) esto reduce el problema de “se quedó cacheado”.
  // Si existe una nueva versión, se toma al iniciar, sin mensajes obligatorios.

  const UPDATE_BOOT_WINDOW_MS = 15000; // ventana en la que permitimos auto-actualizar (solo arranque)
  const bootTime = Date.now(); // marca de tiempo al cargar la app

  // Si ya existía un SW controlando la app, consideramos que es un update (no primera instalación).
  const hadControllerOnBoot = typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && !!navigator.serviceWorker.controller;

  // Evitar recargas múltiples.
  let reloading = false;

  // Control simple del indicador de actualización.
  // Solo lo encendemos si es un update real (ya existía controller al abrir).
  const setUpdating = (isUpdating: boolean) => {
    if (!hadControllerOnBoot) return;
    const withinBootWindow = (Date.now() - bootTime) <= UPDATE_BOOT_WINDOW_MS;
    if (!withinBootWindow) return;
    emitSwUpdateStatus(isUpdating ? 'updating' : 'idle');
  };

  // Cuando el SW nuevo toma control, recargamos una sola vez para servir la nueva versión.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      const withinBootWindow = (Date.now() - bootTime) <= UPDATE_BOOT_WINDOW_MS;
      if (!withinBootWindow) return; // No interrumpir al usuario fuera del arranque.
      if (!hadControllerOnBoot) return; // Primera instalación: no recargar.
      if (reloading) return;
      reloading = true;
      setUpdating(true);
      window.location.reload();
    });
  }

  serviceWorkerRegistration.register({
    onUpdateFound: (_) => {
      // Apenas se detecta el update (descarga/instalación), mostramos "Actualizando...".
      setUpdating(true);
    },
    onUpdate: (registration) => {
      // Solo aplicamos el update automáticamente durante el arranque.
      // Si el navegador detecta un update más tarde, no forzamos nada.
      const withinBootWindow = (Date.now() - bootTime) <= UPDATE_BOOT_WINDOW_MS;
      if (!withinBootWindow) return;

      // Pedimos al SW en espera que active inmediatamente.
      // Al activar, disparará controllerchange y recargaremos 1 vez.
      if (registration?.waiting) {
        setUpdating(true);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    },
  });

  // Chequeo proactivo al abrir: pide al navegador que busque una nueva versión del SW.
  // Esto ayuda a que el update se aplique en el mismo arranque.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.update())
      .then(() => {
        // Si no hubo update, asegurar que el indicador quede apagado.
        setUpdating(false);
      })
      .catch(() => {
        setUpdating(false);
      });
  }
}

// Canal lateral para detectar deploys aunque el SW quede pegado en cache.
// Usa /version.json + header X-App-Version del backend (ver utils/versionCheck.ts).
void initVersionCheck();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
